import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const Pcardb = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [pcardbData, setPcardbData] = useState(null);

  useEffect(() => {
    fetchPcardbData();
  }, []);

  const fetchPcardbData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/pcardb/get_pcardb_content`);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setPcardbData(result.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching PCARDB data:', error);
      setLoading(false);
    }
  };

  const content = {
    en: {
      title: "Primary Cooperative Agricultural and Rural Development Banks",
      overview: "Overview",
      structure: "Institutional Structure",
      keyStats: "Key Statistics",
      g2cServices: "Services to Citizens (G2C Services)",
      coreServices: "Core Services Provided",
      policy: "Policy and Governance"
    },
    ta: {
      title: "முதன்மை கூட்டுறவு விவசாய மற்றும் கிராமப்புற மேம்பாட்டு வங்கிகள்",
      overview: "கண்ணோட்டம்",
      structure: "நிறுவன அமைப்பு",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      g2cServices: "குடிமக்களுக்கான சேவைகள் (G2C சேவைகள்)",
      coreServices: "முக்கிய சேவைகள் வழங்கப்படுகின்றன",
      policy: "கொள்கை மற்றும் நிர்வாகம்"
    }
  };

  const t = content[language];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!pcardbData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">No PCARDB data available</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Simple Header */}
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-800 text-center">
            {t.title}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-4 py-8">

        {/* ===== PCARDB SECTION ===== */}
        <section id="pcardb-section" className="bg-white p-8 rounded-lg shadow-sm">

          {/* OVERVIEW */}
          <div id="pcardb-overview" className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.overview}</h3>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {language === 'en' ? pcardbData.overview_eng : pcardbData.overview_tamil}
              </p>
            </div>
          </div>

          {/* INSTITUTIONAL STRUCTURE */}
          {pcardbData.institutional_structure && (
            <div id="pcardb-structure" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.structure}</h3>
              <div className="bg-purple-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-4">
                  {language === 'en' ? pcardbData.institutional_structure.header_eng : pcardbData.institutional_structure.header_tamil}
                </p>
                {pcardbData.institutional_structure.points_json_data && (
                  <ul className="space-y-3">
                    {pcardbData.institutional_structure.points_json_data.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-600 mr-3 font-bold">•</span>
                        <div>
                          <span className="text-gray-800 font-semibold">
                            {language === 'en' ? item.head_eng : item.head_tamil}
                          </span>
                          <span className="text-gray-700">
                            {' '}{language === 'en' ? item.content_eng : item.content_tamil}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* KEY STATISTICS */}
          {pcardbData.key_stats && (
            <div id="pcardb-stats" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyStats}</h3>
              <div className="bg-green-50 p-6 rounded-lg">
                {pcardbData.key_stats.key_stats_data && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {pcardbData.key_stats.key_stats_data.map((stat, index) => (
                      <div key={index} className="bg-blue-900 p-4 rounded-lg shadow-sm text-center">
                        <div className="font-bold text-white mb-2">
                          <span className="text-2xl">
                            {language === 'en' ? stat.head_eng : stat.head_tamil}
                          </span>
                        </div>
                        <p className="text-sm text-blue-100">
                          {language === 'en' ? stat.content_eng : stat.content_tamil}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {pcardbData.key_stats.footer_eng && (
                  <p className="text-gray-600 text-sm italic mt-4">
                    {language === 'en' ? pcardbData.key_stats.footer_eng : pcardbData.key_stats.footer_tamil}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* G2C SERVICES */}
          {pcardbData.services_to_citizens && pcardbData.services_to_citizens.length > 0 && (
            <div id="pcardb-g2c" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.g2cServices}</h3>
              <div className="bg-amber-50 p-6 rounded-lg">
                {pcardbData.services_to_citizens.map((service, index) => (
                  <p key={index} className="text-gray-700 mb-3">
                    {language === 'en' ? service.content_eng : service.content_tamil}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* CORE SERVICES PROVIDED */}
          {pcardbData.core_services_provided && pcardbData.core_services_provided.length > 0 && (
            <div id="pcardb-services" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.coreServices}</h3>
              <div className="bg-teal-50 p-6 rounded-lg">
                <ul className="space-y-3">
                  {pcardbData.core_services_provided.map((service, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-teal-600 mr-3">•</span>
                      <span className="text-gray-700">
                        {language === 'en' ? service.content_eng : service.content_tamil}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* POLICY & GOVERNANCE */}
          {/* {pcardbData.Policy_and_Governance && pcardbData.Policy_and_Governance.length > 0 && (
            <div id="pcardb-policy">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.policy}</h3>
              <div className="bg-indigo-50 p-6 rounded-lg">
                {pcardbData.Policy_and_Governance.map((policy, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-3">
                    {language === 'en' ? policy.content_eng : policy.content_tamil}
                  </p>
                ))}
              </div>
            </div>
          )} */}

        </section>

      </div>
    </div>
  );
};

export default Pcardb;